/**
 * ProfileCalendar
 *
 * Calendar view filtered by a user's tasks.
 * Reuses the useCalendar hook with tasks filtered for the target user.
 */

import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Columns,
  Clock,
} from 'lucide-react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useCalendar, type CalendarDay } from '@/hooks/useCalendar';
import { isCompletedStatus } from '@/constants/taskStatuses';
import type { Task } from '@/db';

interface ProfileCalendarProps {
  tasks: (Task & { id: string })[];
  onTaskClick?: (task: Task) => void;
}

const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export function ProfileCalendar({ tasks, onTaskClick }: ProfileCalendarProps) {
  const {
    viewMode,
    calendarWeeks,
    calendarDays,
    monthLabel,
    weekLabel,
    dayLabel,
    goToNext,
    goToPrev,
    goToToday,
    setViewMode,
    getTotalTasksInView,
  } = useCalendar({ tasks });

  const currentLabel =
    viewMode === 'month' ? monthLabel : viewMode === 'week' ? weekLabel : dayLabel;

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-surface-secondary rounded-lg border border-border-primary p-0.5">
            <button
              onClick={() => setViewMode('month')}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                viewMode === 'month'
                  ? 'bg-surface-primary text-text-primary shadow-sm'
                  : 'text-text-tertiary hover:text-text-secondary'
              )}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Mese</span>
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                viewMode === 'week'
                  ? 'bg-surface-primary text-text-primary shadow-sm'
                  : 'text-text-tertiary hover:text-text-secondary'
              )}
            >
              <Columns className="w-3.5 h-3.5" />
              <span>Settimana</span>
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium transition-all',
                viewMode === 'day'
                  ? 'bg-surface-primary text-text-primary shadow-sm'
                  : 'text-text-tertiary hover:text-text-secondary'
              )}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Giorno</span>
            </button>
          </div>

          <span className="text-[10px] text-text-quaternary">
            {getTotalTasksInView()} task
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-accent-violet bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
          >
            Oggi
          </button>

          <div className="flex items-center gap-1 bg-surface-secondary rounded-lg border border-border-primary">
            <button
              onClick={goToPrev}
              className="p-1.5 hover:bg-surface-hover rounded-l-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-text-tertiary" />
            </button>
            <span className="px-3 py-1 text-sm font-medium text-text-primary min-w-[180px] text-center">
              {currentLabel}
            </span>
            <button
              onClick={goToNext}
              className="p-1.5 hover:bg-surface-hover rounded-r-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-text-tertiary" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      {viewMode === 'month' && (
        <ProfileMonthGrid
          weeks={calendarWeeks}
          onTaskClick={onTaskClick}
        />
      )}
      {viewMode === 'week' && (
        <ProfileWeekGrid days={calendarDays} onTaskClick={onTaskClick} />
      )}
      {viewMode === 'day' && calendarDays[0] && (
        <ProfileDayView day={calendarDays[0]} onTaskClick={onTaskClick} />
      )}
    </div>
  );
}

// --- Month Grid ---

function ProfileMonthGrid({
  weeks,
  onTaskClick,
}: {
  weeks: { days: CalendarDay[]; weekNumber: number }[];
  onTaskClick?: (task: Task) => void;
}) {
  return (
    <div className="bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border-primary">
        {dayNames.map((name, index) => (
          <div
            key={name}
            className={clsx(
              'px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide',
              index >= 5 ? 'text-text-quaternary' : 'text-text-tertiary'
            )}
          >
            {name}
          </div>
        ))}
      </div>

      <div
        className="grid"
        style={{ gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}
      >
        {weeks.map((week) => (
          <div
            key={week.weekNumber}
            className="grid grid-cols-7 border-b border-border-primary last:border-b-0"
          >
            {week.days.map((day) => (
              <ProfileDayCell
                key={day.date.toISOString()}
                day={day}
                onTaskClick={onTaskClick}
                compact
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Week Grid ---

function ProfileWeekGrid({
  days,
  onTaskClick,
}: {
  days: CalendarDay[];
  onTaskClick?: (task: Task) => void;
}) {
  return (
    <div className="flex gap-2">
      {days.slice(0, 7).map((day) => (
        <div
          key={day.date.toISOString()}
          className="flex-1 flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden"
        >
          <div
            className={clsx(
              'px-3 py-2 border-b border-border-primary text-center',
              day.isToday && 'bg-accent-violet/10'
            )}
          >
            <div className="text-[10px] text-text-tertiary uppercase">
              {format(day.date, 'EEE', { locale: it })}
            </div>
            <div
              className={clsx(
                'text-lg font-semibold',
                day.isToday ? 'text-accent-violet' : 'text-text-primary'
              )}
            >
              {format(day.date, 'd')}
            </div>
          </div>
          <ProfileDayCell day={day} onTaskClick={onTaskClick} />
        </div>
      ))}
    </div>
  );
}

// --- Day View ---

function ProfileDayView({
  day,
  onTaskClick,
}: {
  day: CalendarDay;
  onTaskClick?: (task: Task) => void;
}) {
  return (
    <div className="bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      <div
        className={clsx(
          'px-4 py-3 border-b border-border-primary',
          day.isToday && 'bg-accent-violet/10'
        )}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] text-text-tertiary uppercase">
              {format(day.date, 'EEEE', { locale: it })}
            </div>
            <div
              className={clsx(
                'text-2xl font-semibold',
                day.isToday ? 'text-accent-violet' : 'text-text-primary'
              )}
            >
              {format(day.date, 'd MMMM yyyy', { locale: it })}
            </div>
          </div>
          <div className="text-sm text-text-tertiary">{day.tasks.length} task</div>
        </div>
      </div>
      <ProfileDayCell day={day} onTaskClick={onTaskClick} />
    </div>
  );
}

// --- Day Cell ---

function ProfileDayCell({
  day,
  onTaskClick,
  compact = false,
}: {
  day: CalendarDay;
  onTaskClick?: (task: Task) => void;
  compact?: boolean;
}) {
  const maxVisible = compact ? 3 : 100;
  const visibleTasks = day.tasks.slice(0, maxVisible);
  const hiddenCount = day.tasks.length - visibleTasks.length;

  const priorityColors: Record<string, string> = {
    urgent: 'border-l-accent-rose',
    high: 'border-l-accent-amber',
    normal: 'border-l-accent-blue',
    low: 'border-l-accent-emerald',
  };

  return (
    <div
      className={clsx(
        'flex-1 p-1 border-r border-border-primary last:border-r-0 min-h-[80px]',
        !day.isCurrentMonth && 'bg-surface-tertiary/20',
        day.isWeekend && 'bg-surface-tertiary/10'
      )}
    >
      {compact && (
        <div
          className={clsx(
            'flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium mb-1',
            day.isToday
              ? 'bg-accent-violet text-white'
              : !day.isCurrentMonth
                ? 'text-text-quaternary'
                : 'text-text-secondary'
          )}
        >
          {format(day.date, 'd')}
        </div>
      )}

      <div className="space-y-1">
        {visibleTasks.map((task) => {
          const isCompleted = isCompletedStatus(task.status);
          return (
            <div
              key={task.id}
              onClick={() => onTaskClick?.(task)}
              className={clsx(
                'px-2 py-1 rounded text-[10px] font-medium cursor-pointer transition-all border-l-2',
                isCompleted
                  ? 'bg-surface-tertiary/50 text-text-tertiary line-through'
                  : 'bg-surface-primary text-text-primary hover:shadow-sm',
                task.priority
                  ? priorityColors[task.priority]
                  : 'border-l-border-primary'
              )}
            >
              <span className="truncate block">{task.name}</span>
            </div>
          );
        })}

        {hiddenCount > 0 && (
          <div className="text-[10px] text-text-tertiary text-center py-0.5">
            +{hiddenCount} altri
          </div>
        )}
      </div>
    </div>
  );
}
